import React, { useState } from "react";
import { getDeviceTypeMeta } from "../../config/ccDeviceTypes";

/**
 * YouLaberShared —— 高端视觉的实验员卡片网格 + 密码输入卡。
 * 同时给 YouLaberPanel（独立 tab）和 CCDeviceSelector（ccma 兜底 Dialog）使用。
 */

const STATUS_THEME = {
  online:  { dot: "#3fb950", text: "空闲", pulse: true,  cardBg: "rgba(63,185,80,0.06)",  cardBorder: "rgba(63,185,80,0.35)" },
  busy:    { dot: "#f0883e", text: "忙碌", pulse: false, cardBg: "rgba(240,136,62,0.05)", cardBorder: "rgba(240,136,62,0.30)" },
  offline: { dot: "#6e7681", text: "离线", pulse: false, cardBg: "rgba(255,255,255,0.02)", cardBorder: "rgba(110,118,129,0.25)" },
};

function avatarFromName(name) {
  if (!name) return "?";
  const ch = name.trim().charAt(0);
  return ch || "?";
}

function gradientFromString(s) {
  let h = 0;
  for (let i = 0; i < (s || "").length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  const hue = h % 360;
  return `linear-gradient(135deg, hsl(${hue},65%,42%) 0%, hsl(${(hue + 40) % 360},70%,32%) 100%)`;
}

/** 单张实验员卡片 */
export const DeviceCard = ({ device, onClick, compact = false }) => {
  const status = STATUS_THEME[device.status] || STATUS_THEME.offline;
  const meta = getDeviceTypeMeta(device.device_type);
  const isOffline = device.status === "offline";
  const isBusy = device.status === "busy";

  return (
    <button
      type="button"
      onClick={() => !isOffline && onClick && onClick(device)}
      disabled={isOffline}
      className="youlaber-device-card"
      style={{
        all: "unset",
        cursor: isOffline ? "not-allowed" : "pointer",
        display: "flex",
        flexDirection: "column",
        gap: compact ? "10px" : "12px",
        padding: compact ? "14px" : "18px",
        borderRadius: "12px",
        background: `linear-gradient(180deg, rgba(22,27,34,0.85), rgba(13,17,23,0.85)), ${status.cardBg}`,
        border: `1px solid ${status.cardBorder}`,
        boxShadow: isOffline
          ? "none"
          : "0 1px 0 rgba(255,255,255,0.04) inset, 0 6px 18px rgba(0,0,0,0.25)",
        opacity: isOffline ? 0.55 : 1,
        transition: "transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease",
        position: "relative",
        overflow: "hidden",
      }}
      onMouseEnter={(e) => {
        if (isOffline) return;
        e.currentTarget.style.transform = "translateY(-2px)";
        e.currentTarget.style.borderColor = "rgba(88,166,255,0.55)";
      }}
      onMouseLeave={(e) => {
        if (isOffline) return;
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.borderColor = status.cardBorder;
      }}
    >
      {/* 顶角课程徽标：直接显示 bridge 上报的 device_type */}
      {device.device_type && (
        <div style={{
          position: "absolute", top: 10, right: 10,
          maxWidth: "60%",
          fontSize: "10px", color: meta.accent,
          background: `${meta.accent}1f`, border: `1px solid ${meta.accent}55`,
          padding: "1px 8px", borderRadius: "999px", fontWeight: 600, letterSpacing: "0.3px",
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}>
          {meta.icon} {device.device_type}
        </div>
      )}

      {/* 头像 + 名称 */}
      <div style={{ display: "flex", alignItems: "center", gap: "12px", marginTop: "4px" }}>
        <div style={{
          width: "44px", height: "44px", borderRadius: "12px",
          background: gradientFromString(device.device_name || device.device_id),
          display: "flex", alignItems: "center", justifyContent: "center",
          color: "#fff", fontSize: "18px", fontWeight: 700,
          boxShadow: "0 2px 6px rgba(0,0,0,0.35), 0 0 0 1px rgba(255,255,255,0.06) inset",
          flexShrink: 0,
        }}>
          {avatarFromName(device.device_name)}
        </div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{
            color: "#e6edf3", fontSize: "14px", fontWeight: 600,
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          }}>
            {device.device_name || device.device_id}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "6px", marginTop: "3px" }}>
            <span style={{
              width: "7px", height: "7px", borderRadius: "50%",
              background: status.dot, display: "inline-block",
              boxShadow: status.pulse ? `0 0 6px ${status.dot}` : "none",
            }} />
            <span style={{ color: status.dot, fontSize: "11px", fontWeight: 500 }}>
              {status.text}
              {isBusy && device.queue_length > 0 && `（排队 ${device.queue_length}）`}
            </span>
            {device.has_password && (
              <span style={{ color: "#8b949e", fontSize: "10px", marginLeft: "4px" }}>🔒</span>
            )}
          </div>
        </div>
      </div>

      {/* 标签/实验室 */}
      {(device.lab || (device.tags && device.tags.length > 0)) && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "4px", minHeight: "16px" }}>
          {device.lab && (
            <span style={{
              fontSize: "10px", color: "#8b949e",
              background: "rgba(255,255,255,0.04)", padding: "1px 6px", borderRadius: "4px",
            }}>📍 {device.lab}</span>
          )}
          {(device.tags || []).slice(0, 3).map((tag) => (
            <span key={tag} style={{
              fontSize: "10px", color: "#8b949e",
              background: "rgba(255,255,255,0.04)", padding: "1px 6px", borderRadius: "4px",
            }}>{tag}</span>
          ))}
        </div>
      )}

      {/* 主操作行 */}
      <div style={{
        display: "flex", justifyContent: "space-between", alignItems: "center",
        marginTop: "auto", paddingTop: "4px",
      }}>
        <span style={{ color: "#6e7681", fontSize: "10px", fontFamily: "'Courier New', monospace" }}>
          {(device.device_id || "").replace(/^(cc-|YouLaber-|YouLabber-)/, "").slice(0, 10)}
        </span>
        <span style={{
          fontSize: "11px", fontWeight: 600,
          color: isOffline ? "#6e7681" : (isBusy ? "#f0883e" : "#58a6ff"),
        }}>
          {isOffline ? "不可用" : (isBusy ? "排队接入 →" : "立即接入 →")}
        </span>
      </div>
    </button>
  );
};

/** 实验员卡片网格容器 */
export const DeviceGrid = ({ devices, onSelect, emptyHint = "暂无可用的 YouLabber 实验员" }) => {
  if (!devices || devices.length === 0) {
    return (
      <div style={{
        padding: "40px 20px", textAlign: "center", color: "#6e7681",
        fontSize: "13px", border: "1px dashed rgba(255,255,255,0.08)",
        borderRadius: "10px",
      }}>
        {emptyHint}
      </div>
    );
  }
  return (
    <div style={{
      display: "grid",
      gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
      gap: "12px",
    }}>
      {devices.map((d) => (
        <DeviceCard key={d.device_id} device={d} onClick={onSelect} />
      ))}
    </div>
  );
};

/** 高端视觉的密码确认卡 */
export const PasswordCard = ({ device, onConfirm, onCancel, error }) => {
  const [pwd, setPwd] = useState("");
  const meta = getDeviceTypeMeta(device?.device_type);

  const submit = () => {
    if (!pwd.trim()) return;
    onConfirm(pwd.trim());
  };

  return (
    <div style={{
      maxWidth: "440px", margin: "0 auto",
      background: "linear-gradient(180deg, #161b22 0%, #0d1117 100%)",
      border: "1px solid rgba(88,166,255,0.25)",
      borderRadius: "14px",
      padding: "22px",
      boxShadow: "0 12px 40px rgba(0,0,0,0.45), 0 0 0 1px rgba(88,166,255,0.08) inset",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "16px" }}>
        <div style={{
          width: "44px", height: "44px", borderRadius: "12px",
          background: gradientFromString(device?.device_name || device?.device_id),
          display: "flex", alignItems: "center", justifyContent: "center",
          color: "#fff", fontSize: "18px", fontWeight: 700,
        }}>
          {avatarFromName(device?.device_name)}
        </div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ color: "#e6edf3", fontSize: "15px", fontWeight: 600 }}>
            接入 {device?.device_name}
          </div>
          {device?.device_type && (
            <div style={{ color: meta.accent, fontSize: "11px", marginTop: "2px" }}>
              {meta.icon} {device.device_type}
            </div>
          )}
        </div>
      </div>

      <label style={{ display: "block", color: "#8b949e", fontSize: "12px", marginBottom: "6px" }}>
        🔒 实验员访问密码
      </label>
      <input
        autoFocus
        type="password"
        value={pwd}
        onChange={(e) => setPwd(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && submit()}
        placeholder="请输入由实验员所有者设置的密码"
        style={{
          width: "100%", boxSizing: "border-box",
          background: "#0d1117",
          border: `1px solid ${error ? "#f85149" : "rgba(255,255,255,0.12)"}`,
          borderRadius: "8px",
          color: "#e6edf3",
          padding: "10px 12px",
          fontSize: "13px",
          outline: "none",
          fontFamily: "inherit",
        }}
      />
      {error && (
        <div style={{ color: "#f85149", fontSize: "11px", marginTop: "6px" }}>{error}</div>
      )}

      <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end", marginTop: "18px" }}>
        <button
          onClick={onCancel}
          style={{
            background: "transparent", border: "1px solid rgba(255,255,255,0.15)",
            color: "#9aa4b2", padding: "7px 16px", borderRadius: "7px",
            fontSize: "12px", cursor: "pointer", fontFamily: "inherit",
          }}
        >返回</button>
        <button
          onClick={submit}
          disabled={!pwd.trim()}
          style={{
            background: pwd.trim()
              ? "linear-gradient(180deg, #2da043 0%, #1f7a32 100%)"
              : "#21262d",
            border: "none",
            color: "#fff", padding: "7px 18px", borderRadius: "7px",
            fontSize: "12px", fontWeight: 600,
            cursor: pwd.trim() ? "pointer" : "not-allowed",
            fontFamily: "inherit",
            boxShadow: pwd.trim() ? "0 2px 6px rgba(45,160,67,0.35)" : "none",
          }}
        >确认接入</button>
      </div>
    </div>
  );
};

export default { DeviceGrid, DeviceCard, PasswordCard };
